import React, { useCallback, useEffect, useRef, useState } from 'react';
import { api } from '../lib/api';
import { toast } from 'sonner';
import { Button } from './ui/button';
import { Input } from './ui/input';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from './ui/dialog';
import { Loader2, PenLine, Upload, X, Copy, RefreshCw, Undo2, FileText, QrCode, Mail, FlaskConical, ChevronLeft, ChevronRight, Timer, Crosshair, Plus, Download } from 'lucide-react';
import { DeviceAgentLog } from './DeviceAgentLog';

// DIN envelope formats (w × h in mm as fed into the iAuto; reading orientation via rotation)
export const ENVELOPE_PRESETS = [
  ['c4', 'C4 — 324 × 229 mm (A4 ungefaltet)', 324, 229],
  ['b4', 'B4 — 353 × 250 mm', 353, 250],
  ['c5', 'C5 — 229 × 162 mm (A4 einmal gefaltet)', 229, 162],
  ['b5', 'B5 — 250 × 176 mm', 250, 176],
  ['c65', 'C6/5 (DIN lang+) — 229 × 114 mm', 229, 114],
  ['dl', 'DL (DIN lang) — 220 × 110 mm', 220, 110],
  ['dl_s', 'DIN lang kompakt — 210 × 105 mm', 210, 105],
  ['c6', 'C6 — 162 × 114 mm (A4 zweimal gefaltet)', 162, 114],
];
const presetFor = (w, h) => {
  const W = Math.round(parseFloat(w) || 0), H = Math.round(parseFloat(h) || 0);
  const hit = ENVELOPE_PRESETS.find(([, , pw, ph]) => (pw === W && ph === H) || (pw === H && ph === W));
  return hit ? hit[0] : 'custom';
};

function EnvelopeFormatPicker({ form, setField, prefix, testId }) {
  // prefix 'env' (standard letters) or 'benv' (break-ups). Rotation 90/270 = fed portrait → page w/h swapped.
  const rotKey = `${prefix}_rotation`, wKey = `${prefix}_page_w`, hKey = `${prefix}_page_h`;
  const rot = Math.round(parseFloat(form[rotKey]) || 0);
  const portrait = rot === 90 || rot === 270;
  const current = presetFor(form[wKey], form[hKey]);
  const apply = (id, nextRot = rot) => {
    const p = ENVELOPE_PRESETS.find(x => x[0] === id);
    if (!p) return;
    const [, , pw, ph] = p;
    const vertical = nextRot === 90 || nextRot === 270;
    setField(wKey, vertical ? Math.min(pw, ph) : pw);
    setField(hKey, vertical ? Math.max(pw, ph) : ph);
    // reading-orientation size → sensible address Y (40 % of height); X is handled by Auto-Fit
    const readH = Math.min(pw, ph);
    if (prefix === 'benv') { setField('benv_addr_x', Math.round(pw * 0.45)); setField('benv_addr_y', Math.round(readH * 0.35)); }
    else setField('env_pos_addr', null);
  };
  const setRot = (r) => {
    setField(rotKey, r);
    if (current !== 'custom') apply(current, r);
    else { const w = form[wKey], h = form[hKey]; setField(wKey, h); setField(hKey, w); }
  };
  return (
    <div className="border border-border rounded-sm p-3 space-y-2.5" data-testid={`${testId}-format-box`}>
      <p className="text-[11px] uppercase tracking-wider font-mono text-muted-foreground">Umschlagformat</p>
      <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
        <div>
          <label className="text-[10px] uppercase tracking-wider font-mono text-muted-foreground">Format</label>
          <select value={current} data-testid={`${testId}-format-select`}
            onChange={e => e.target.value !== 'custom' && apply(e.target.value)}
            className="mt-1 w-full h-8 text-xs rounded-sm border border-border bg-secondary/40 text-foreground px-2">
            {ENVELOPE_PRESETS.map(([id, label]) => <option key={id} value={id}>{label}</option>)}
            <option value="custom">Eigenes Format — Breite/Höhe oben eintragen</option>
          </select>
        </div>
        <div>
          <label className="text-[10px] uppercase tracking-wider font-mono text-muted-foreground">Einzug in die iAuto</label>
          <select value={String(rot)} data-testid={`${testId}-feed-select`}
            onChange={e => setRot(parseFloat(e.target.value))}
            className="mt-1 w-full h-8 text-xs rounded-sm border border-border bg-secondary/40 text-foreground px-2">
            <option value="0">Quer (Adresse wird normal geschrieben)</option>
            <option value="90">Hochkant, Oberkante links (Adresse 90° gedreht)</option>
            <option value="270">Hochkant, Oberkante rechts (Adresse 270° gedreht)</option>
          </select>
        </div>
      </div>
      <p className="text-[10px] text-muted-foreground">
        Aktuell {form[wKey]} × {form[hKey]} mm{portrait ? ' (hochkant eingezogen — Adresse wird gedreht geschrieben und ist auf dem Umschlag normal lesbar)' : ''}.
        Beim Formatwechsel wird die Adressposition auf einen sinnvollen Standard gesetzt — danach in der Vorschau prüfen.
      </p>
    </div>
  );
}

const DOC_TABS = [
  ['letter', 'Brief', FileText],
  ['bletter', 'Break-up-Brief', FileText],
  ['flyer', 'Flyer', QrCode],
  ['envelope', 'Umschlag', Mail],
  ['benv', 'Break-up-Umschlag', Mail],
];
const FIELDS_BY_TAB = {
  letter: [
    ['font_size', 'Font size (mm)'], ['line_height', 'Line height (mm)'], ['para_spacing', 'Paragraph gap (mm)'],
    ['letter_spacing', 'Letter spacing (mm)'], ['word_spacing', 'Word spacing (mm)'], ['naturalness', 'Naturalness (%)'],
    ['punct_gap_mm', 'Abstand vor . , ; : (mm) — alle Dokumente'],
    ['jitter_line_start', 'Zeilenstart-Versatz (mm)'], ['jitter_word_spacing', 'Wortabstand-Variation (%)'],
    ['baseline_drift', 'Baseline-Welle (mm)'], ['jitter_line_height', 'Zeilenabstand-Variation (%)'],
    ['margin_top', 'Margin top (mm)'], ['margin_left', 'Margin left (mm)'], ['margin_right', 'Margin right (mm)'],
    ['margin_bottom', 'Margin bottom (mm)'], ['xpos_mm', 'iAuto X offset (mm)'], ['ypos_mm', 'iAuto Y offset (mm)'],
    ['signature_height_mm', 'Signature height (mm)'], ['signature_gap_mm', 'Signature gap (mm)'],
    ['writing_speed_mms', 'Pen speed (mm/s)'], ['auto_fit_min_font', 'Auto-Fit: min. Schrift (mm)'],
  ],
  bletter: [
    ['bl_font_size', 'Font size (mm)'], ['bl_line_height', 'Line height (mm)'], ['bl_para_spacing', 'Paragraph gap (mm)'],
    ['bl_letter_spacing', 'Letter spacing (mm)'], ['bl_word_spacing', 'Word spacing (mm)'],
    ['bl_margin_top', 'Margin top (mm)'], ['bl_margin_left', 'Margin left (mm)'], ['bl_margin_right', 'Margin right (mm)'],
    ['bl_margin_bottom', 'Margin bottom (mm)'], ['bl_signature_height_mm', 'Signature height (mm)'],
    ['bl_signature_gap_mm', 'Signature gap (mm)'],
    ['bl_qr_dx_mm', 'QR Versatz X (mm)'], ['bl_qr_dy_mm', 'QR Versatz Y (mm)'], ['bl_qr_size_mm', 'QR-Feld Größe (mm)'],
  ],
  flyer: [
    ['flyer_font_size', 'Font size (mm)'], ['flyer_line_height', 'Line height (mm)'],
    ['flyer_msg_width', 'Message width (mm)'], ['flyer_signature_height_mm', 'Signature height (mm)'],
  ],
  envelope: [
    ['env_font_size', 'Font size (mm)'], ['env_line_height', 'Line height (mm)'],
    ['env_page_w', 'Envelope width (mm)'], ['env_page_h', 'Envelope height (mm)'],
    ['env_right_margin_mm', 'Rechter Rand (mm) — Auto-Fit'],
  ],
  benv: [
    ['benv_font_size', 'Font size (mm)'], ['benv_line_height', 'Line height (mm)'],
    ['benv_page_w', 'Umschlag Breite (mm)'], ['benv_page_h', 'Umschlag Höhe (mm)'],
    ['benv_addr_x', 'Adresse X (mm, Leserichtung)'], ['benv_addr_y', 'Adresse Y (mm, Leserichtung)'],
    ['benv_right_margin_mm', 'Rechter Rand (mm) — Auto-Fit'],
  ],
};
const ALL_NUM_FIELDS = [...FIELDS_BY_TAB.letter, ...FIELDS_BY_TAB.bletter, ...FIELDS_BY_TAB.flyer, ...FIELDS_BY_TAB.envelope, ...FIELDS_BY_TAB.benv];
const POS_KEYS = ['pos_header', 'pos_date', 'pos_signature', 'flyer_pos_msg', 'flyer_pos_wenden', 'env_pos_addr', 'bl_pos_date', 'bl_pos_signature'];
const ELEMENTS = {
  header: { label: 'Header', cls: 'border-sky-400', badge: 'bg-sky-500', posKey: 'pos_header', tab: 'letter' },
  date: { label: 'Date', cls: 'border-amber-400', badge: 'bg-amber-500', posKey: 'pos_date', tab: 'letter' },
  body: { label: 'Text', cls: 'border-emerald-400', badge: 'bg-emerald-500', posKey: null, tab: 'letter' },
  signature: { label: 'Signature', cls: 'border-purple-400', badge: 'bg-purple-500', posKey: 'pos_signature', tab: 'letter' },
  bl_date: { el: 'date', label: 'Date', cls: 'border-amber-400', badge: 'bg-amber-500', posKey: 'bl_pos_date', tab: 'bletter' },
  bl_body: { el: 'body', label: 'Text', cls: 'border-emerald-400', badge: 'bg-emerald-500', posKey: null, tab: 'bletter' },
  bl_signature: { el: 'signature', label: 'Signature', cls: 'border-purple-400', badge: 'bg-purple-500', posKey: 'bl_pos_signature', tab: 'bletter' },
  flyer_msg: { label: 'Message', cls: 'border-emerald-400', badge: 'bg-emerald-500', posKey: 'flyer_pos_msg', tab: 'flyer' },
  flyer_wenden: { label: 'bitte wenden', cls: 'border-amber-400', badge: 'bg-amber-500', posKey: 'flyer_pos_wenden', tab: 'flyer' },
  env_addr: { label: 'Recipient', cls: 'border-emerald-400', badge: 'bg-emerald-500', posKey: 'env_pos_addr', tab: 'envelope' },
  benv_addr: { el: 'env_addr', label: 'Recipient', cls: 'border-emerald-400', badge: 'bg-emerald-500', posKey: 'benv_addr', tab: 'benv' },
};

export const PrintSettingsDialog = ({ open, onOpenChange, settings, onSaved }) => {
  const [tab, setTab] = useState('letter');
  const [form, setForm] = useState({});
  const [saving, setSaving] = useState(false);
  const [testing, setTesting] = useState(false);
  const [blQrBusy, setBlQrBusy] = useState(false);
  const [previewLetters, setPreviewLetters] = useState([]);
  const [previewSvg, setPreviewSvg] = useState('');
  const [elements, setElements] = useState({});
  const [pageDims, setPageDims] = useState({ w: 210, h: 297 });
  const [previewLoading, setPreviewLoading] = useState(false);
  const [previewPage, setPreviewPage] = useState(1);
  const [previewPages, setPreviewPages] = useState(1);
  const [estSeconds, setEstSeconds] = useState(0);
  const [previewFit, setPreviewFit] = useState(null);
  const pageNumRef = useRef(1);
  const [drag, setDrag] = useState(null);
  const dragRef = useRef(null);
  const fileRef = useRef(null);
  const pageRef = useRef(null);
  const timer = useRef(null);
  const tabRef = useRef('letter');
  const [compareOff, setCompareOff] = useState(false);
  const compareRef = useRef(false);

  const HUMANIZE_ZERO = { jitter_line_start: 0, jitter_word_spacing: 0, baseline_drift: 0, jitter_line_height: 0 };

  const refreshPreview = useCallback((data, docTab) => {
    const d = docTab || tabRef.current;
    setPreviewLoading(true);
    const payload = compareRef.current ? { ...data, ...HUMANIZE_ZERO } : data;
    api.printPreviewSample({ ...payload, doc: d, page: pageNumRef.current })
      .then(r => {
        setPreviewSvg(String(r.data.svg).replace(/width="\d+mm" height="\d+mm"/, 'width="100%"'));
        setElements(r.data.elements || {});
        setPageDims({ w: r.data.page_w || 210, h: r.data.page_h || 297 });
        setPreviewPages(r.data.pages || 1);
        setPreviewPage(r.data.page || 1);
        pageNumRef.current = r.data.page || 1;
        setEstSeconds(r.data.est_seconds || 0);
        setPreviewFit(r.data.fit || null);
      })
      .catch(() => setPreviewSvg(''))
      .finally(() => setPreviewLoading(false));
  }, []);

  const toggleCompare = () => {
    const next = !compareRef.current;
    compareRef.current = next;
    setCompareOff(next);
    refreshPreview(form);
  };

  const goPage = (p) => {
    pageNumRef.current = p;
    setPreviewPage(p);
    refreshPreview(form);
  };

  useEffect(() => {
    if (!open || !settings) return;
    const f = {
      iauto_url: settings.iauto_url || '', auto_start: settings.auto_start !== false,
      append_signature: settings.append_signature !== false, show_date: !!settings.show_date,
      hyphenate: settings.hyphenate !== false,
      auto_fit_one_page: settings.auto_fit_one_page !== false,
      body_beside_date: settings.body_beside_date !== false,
      header_text: settings.header_text || '',
      body_start_y: settings.body_start_y ?? null,
      bl_body_start_y: settings.bl_body_start_y ?? null,
      preview_letter_id: '',
      benv_rotation: settings.benv_rotation ?? 90,
      ...POS_KEYS.reduce((acc, k) => ({ ...acc, [k]: settings[k] || null }), {}),
      ...ALL_NUM_FIELDS.reduce((acc, [k]) => ({ ...acc, [k]: settings[k] ?? '' }), {}),
    };
    setForm(f);
    setTab('letter');
    tabRef.current = 'letter';
    refreshPreview(f, 'letter');
    api.printPreviewLetters().then(r => setPreviewLetters(r.data.letters || [])).catch(() => {});
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open]);

  const switchTab = (t) => {
    setTab(t);
    tabRef.current = t;
    pageNumRef.current = 1;
    setPreviewPage(1);
    refreshPreview(form, t);
  };

  const setField = (key, value) => {
    setForm(f => {
      const next = { ...f, [key]: value };
      clearTimeout(timer.current);
      timer.current = setTimeout(() => refreshPreview(next), 600);
      return next;
    });
  };

  // ── drag & drop of elements on the preview ──
  const mmFromEvent = (e) => {
    const r = pageRef.current.getBoundingClientRect();
    return { x: (e.clientX - r.left) / r.width * pageDims.w, y: (e.clientY - r.top) / r.height * pageDims.h };
  };
  // envelopes: the renderer names the block env_addr (fed landscape) or env_addr_fixed (fed portrait, rotated)
  const elFor = (key) => {
    const k = ELEMENTS[key]?.el || key;
    return k === 'env_addr' ? (elements.env_addr || elements.env_addr_fixed) : elements[k];
  };
  // page coords of the dragged box → reading-orientation coords (what the settings store)
  const readingPos = (d, el, rot) => {
    if (rot === 90) return { x: +d.y.toFixed(1), y: +(pageDims.w - d.x - (el.w || 0)).toFixed(1) };
    if (rot === 270) return { x: +(pageDims.h - d.y - (el.h || 0)).toFixed(1), y: +d.x.toFixed(1) };
    return { x: +d.x.toFixed(1), y: +d.y.toFixed(1) };
  };

  const startDrag = (key, e) => {
    e.preventDefault();
    const el = elFor(key);
    if (!el || !pageRef.current) return;
    const m = mmFromEvent(e);
    const d = { key, dx: m.x - el.x, dy: m.y - el.y, x: el.x, y: el.y };
    dragRef.current = d;
    setDrag(d);
  };
  useEffect(() => {
    if (!open) return;
    const move = (e) => {
      const d = dragRef.current;
      if (!d || !pageRef.current) return;
      const m = mmFromEvent(e);
      const nd = { ...d, x: Math.max(0, Math.min(pageDims.w - 5, m.x - d.dx)), y: Math.max(0, Math.min(pageDims.h - 5, m.y - d.dy)) };
      dragRef.current = nd;
      setDrag(nd);
    };
    const up = () => {
      const d = dragRef.current;
      dragRef.current = null;
      setDrag(null);
      if (!d) return;
      if (d.key === 'body') {
        setField('body_start_y', +(d.y + parseFloat(form.font_size || 6)).toFixed(1));
      } else if (d.key === 'bl_body') {
        setField('bl_body_start_y', +(d.y + parseFloat(form.bl_font_size || form.font_size || 6)).toFixed(1));
      } else if (d.key === 'benv_addr') {
        const pos = readingPos(d, elFor('benv_addr') || {}, Math.round(parseFloat(form.benv_rotation) || 0));
        if ((form.benv_anchor || 'right') !== 'right') setField('benv_addr_x', pos.x); // X is auto-fitted when anchored right
        setField('benv_addr_y', pos.y);
      } else if (d.key === 'env_addr') {
        const pos = readingPos(d, elFor('env_addr') || {}, Math.round(parseFloat(form.env_rotation) || 0));
        const prev = form.env_pos_addr || {};
        setField('env_pos_addr', (form.env_anchor || 'right') === 'right' && prev.x != null ? { x: prev.x, y: pos.y } : pos);
      } else if (ELEMENTS[d.key]?.posKey) {
        setField(ELEMENTS[d.key].posKey, { x: +d.x.toFixed(1), y: +d.y.toFixed(1) });
      }
    };
    window.addEventListener('pointermove', move);
    window.addEventListener('pointerup', up);
    return () => { window.removeEventListener('pointermove', move); window.removeEventListener('pointerup', up); };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open, form.font_size, form.bl_font_size, pageDims]);

  const resetPositions = () => {
    const next = { ...form, body_start_y: null, bl_body_start_y: null, ...POS_KEYS.reduce((acc, k) => ({ ...acc, [k]: null }), {}) };
    setForm(next);
    refreshPreview(next);
  };
  const hasCustomPos = form.body_start_y != null || form.bl_body_start_y != null || POS_KEYS.some(k => form[k]);

  const sendTestLetter = async () => {
    if (!window.confirm('Testbrief an die Maschine senden?\n\nEr wird mit den AKTUELLEN Einstellungen (werden dabei gespeichert) gerendert und mit Priorität VOR allen wartenden Jobs geschrieben.')) return;
    setTesting(true);
    try {
      await api.updatePrintSettings(form);
      const r = await api.createTestPrint({ letter_id: form.preview_letter_id || '' });
      toast.success(`Testbrief eingereiht (${r.data.pages} Seite${r.data.pages !== 1 ? 'n' : ''}) — wird als Nächstes geschrieben`);
      onSaved?.();
    } catch (err) { toast.error(err.response?.data?.detail || 'Fehlgeschlagen'); }
    finally { setTesting(false); }
  };

  const downloadBlQrTest = async () => {
    setBlQrBusy(true);
    try {
      await api.updatePrintSettings(form);
      const res = await api.downloadBreakupQrTestPdf();
      const url = URL.createObjectURL(new Blob([res.data], { type: 'application/pdf' }));
      const a = document.createElement('a');
      a.href = url;
      a.download = 'OLTA_Breakup_QR_Kalibrierung.pdf';
      a.click();
      URL.revokeObjectURL(url);
      toast.success('Einstellungen gespeichert & Testseite geladen — auf EIN Break-up-Briefpapier drucken und Rahmen prüfen');
      onSaved?.();
    } catch (err) { toast.error(err.response?.data?.detail || 'Testseite fehlgeschlagen'); }
    finally { setBlQrBusy(false); }
  };

  const saveSettings = async () => {
    setSaving(true);
    try {
      await api.updatePrintSettings(form);
      toast.success('iAuto settings saved');
      onOpenChange(false);
      onSaved?.();
    } catch (err) { toast.error(err.response?.data?.detail || 'Save failed'); }
    finally { setSaving(false); }
  };

  const uploadFont = async (e) => {
    const file = e.target.files?.[0];
    e.target.value = '';
    if (!file) return;
    const fd = new FormData();
    fd.append('file', file);
    try {
      const r = await api.uploadPrintFont(fd);
      toast.success(`Font "${r.data.font_name}" active`);
      if (r.data.warning) toast.warning(r.data.warning);
      onSaved?.();
      refreshPreview(form);
    } catch (err) { toast.error(err.response?.data?.detail || 'Font upload failed'); }
  };

  const removeFont = async () => {
    try { await api.deletePrintFont(); toast.success('Back to the default font'); onSaved?.(); refreshPreview(form); }
    catch { toast.error('Failed'); }
  };

  const addDevice = async () => {
    const name = window.prompt('Name des neuen Rechners / Standorts (z. B. „Zuhause")', 'Zuhause');
    if (!name) return;
    try { await api.createPrintDevice(name); toast.success(`Gerät „${name}" angelegt`); onSaved?.(); }
    catch (e) { toast.error(e.response?.data?.detail || 'Fehlgeschlagen'); }
  };

  const addMachine = async (host) => {
    const name = window.prompt(`Name der zweiten Maschine am Rechner „${host.name}" (z. B. „${host.name} · iAuto B")`, `${host.name} · iAuto B`);
    if (!name) return;
    try { await api.createPrintDevice(name, host.id); toast.success(`„${name}" angelegt — läuft über den Agent von „${host.name}"; unter „Maschine“ jetzt die zweite iAuto zuweisen`); onSaved?.(); }
    catch (e) { toast.error(e.response?.data?.detail || 'Fehlgeschlagen'); }
  };

  const saveDeviceField = async (d, field, value) => {
    if (String(d[field] ?? '') === String(value ?? '')) return;
    try { await api.updatePrintDevice(d.id, { [field]: value === '' ? null : value }); onSaved?.(); }
    catch (e) { toast.error(e.response?.data?.detail || 'Ungültiger Wert'); }
  };

  const activateDevice = async (d) => {
    try { await api.activatePrintDevice(d.id); toast.success(`„${d.name}" ist jetzt aktiv`); onSaved?.(); }
    catch { toast.error('Fehlgeschlagen'); }
  };

  const regenDeviceKey = async (d) => {
    if (!window.confirm('Agent-Key neu erzeugen? Das laufende Agent-Skript dieses Geräts muss danach neu geladen werden.')) return;
    try { await api.regeneratePrintDeviceKey(d.id); toast.success('Neuer Key — Agent-Skript neu herunterladen'); onSaved?.(); }
    catch { toast.error('Fehlgeschlagen'); }
  };

  const deleteDevice = async (d) => {
    if (!window.confirm(`Gerät „${d.name}" löschen? Offene Jobs wandern auf das aktive Gerät.`)) return;
    try { await api.deletePrintDevice(d.id); toast.success('Gerät gelöscht'); onSaved?.(); }
    catch (e) { toast.error(e.response?.data?.detail || 'Fehlgeschlagen'); }
  };

  const agentFileName = (d, platform) => `olta_print_agent_${(d.name || 'device').toLowerCase().replace(/[^a-z0-9]+/g, '_').replace(/^_+|_+$/g, '') || 'device'}.${platform === 'mac' ? 'command' : 'py'}`;
  const downloadDeviceScript = async (d, platform = '') => {
    try {
      const res = await api.downloadPrintAgentScript(d.id, platform);
      const url = URL.createObjectURL(new Blob([res.data], { type: platform === 'mac' ? 'text/x-shellscript' : 'text/x-python' }));
      const a = document.createElement('a');
      a.href = url;
      a.download = agentFileName(d, platform);
      a.click();
      URL.revokeObjectURL(url);
      toast.success(platform === 'mac' ? `Mac-Agent für „${d.name}" geladen — Terminal-Befehl fürs erste Mal steht unter dem Gerät` : `Agent-Skript (Windows/Python) für „${d.name}" geladen`);
    } catch (e) { toast.error(e.response?.data?.detail || 'Download fehlgeschlagen'); }
  };
  const macFirstRun = (d) => { const f = agentFileName(d, 'mac'); return `bash "$(ls -t ~/Downloads/${f.replace(/\.command$/, '')}*.command | head -1)"`; };
  const machineOptions = (d) => {
    const seen = (d.machines_seen || []).map((m) => ({ mid: String(m.mid || ''), status: m.status || '', waiting: m.task_num }));
    if (d.machine_id && !seen.some((m) => m.mid === d.machine_id)) seen.push({ mid: d.machine_id, status: 'zuletzt gewählt', waiting: null });
    return seen.filter((m) => m.mid);
  };
  const machinesOf = (host) => (settings?.devices || []).filter((x) => x.host_id === host.id);

  const copyText = (t) => { navigator.clipboard.writeText(t || ''); toast.success('Kopiert'); };

  const visibleElements = Object.keys(ELEMENTS).filter(k => {
    const el = elFor(k);
    if (ELEMENTS[k].tab !== tab || !el) return false;
    if ((el.page || 1) !== previewPage) return false;
    if (k === 'header') return !!(form.header_text || '').trim();
    if (k === 'date' || k === 'bl_date') return !!form.show_date;
    if (k === 'signature' || k === 'bl_signature') return form.append_signature !== false && form.auto_fit_one_page === false;
    if (k === 'body' || k === 'bl_body') return form.auto_fit_one_page === false;
    return true;
  });

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-5xl max-h-[90vh] overflow-y-auto" data-testid="print-settings-dialog">
        <DialogHeader>
          <DialogTitle className="font-heading text-lg flex items-center gap-2">
            <PenLine className="w-4 h-4 text-purple-400" /> iAuto settings
          </DialogTitle>
        </DialogHeader>
        <div className="flex gap-1">
          {DOC_TABS.map(([key, label, Icon]) => (
            <button key={key} onClick={() => switchTab(key)} data-testid={`print-settings-tab-${key}`}
              className={`flex items-center gap-1.5 px-3 py-1.5 text-xs rounded-sm border transition-colors ${tab === key
                ? 'border-purple-500/60 bg-purple-500/15 text-purple-200'
                : 'border-border text-muted-foreground hover:text-foreground'}`}>
              <Icon className="w-3.5 h-3.5" /> {label}
            </button>
          ))}
        </div>
        <div className="grid grid-cols-1 lg:grid-cols-[1fr,420px] gap-5">
          <div className="space-y-4">
            <div className="grid grid-cols-3 gap-3">
              {FIELDS_BY_TAB[tab].map(([key, label]) => (
                <div key={key}>
                  <label className="text-[10px] uppercase tracking-wider font-mono text-muted-foreground">{label}</label>
                  <Input type="number" step="0.5" value={form[key] ?? ''} data-testid={`print-${key}-input`}
                    onChange={e => setField(key, e.target.value)}
                    className="mt-1 h-8 text-xs rounded-sm" />
                </div>
              ))}
            </div>

            {tab === 'letter' && (
              <div className="border border-border rounded-sm p-3 space-y-2.5">
                <p className="text-[11px] uppercase tracking-wider font-mono text-muted-foreground">Letter layout</p>
                <label className="flex items-center gap-2 text-xs text-foreground cursor-pointer">
                  <input type="checkbox" checked={form.append_signature !== false}
                    onChange={e => setField('append_signature', e.target.checked)}
                    data-testid="print-signature-check" className="accent-purple-500 w-3.5 h-3.5" />
                  Real signature at the end (from the handwriting font)
                </label>
                <label className="flex items-center gap-2 text-xs text-foreground cursor-pointer">
                  <input type="checkbox" checked={!!form.show_date}
                    onChange={e => setField('show_date', e.target.checked)}
                    data-testid="print-date-check" className="accent-purple-500 w-3.5 h-3.5" />
                  Date top right (e.g. “22. Juli 2026”)
                </label>
                <label className="flex items-center gap-2 text-xs text-foreground cursor-pointer">
                  <input type="checkbox" checked={form.hyphenate !== false}
                    onChange={e => setField('hyphenate', e.target.checked)}
                    data-testid="print-hyphenate-check" className="accent-purple-500 w-3.5 h-3.5" />
                  Silbentrennung am Zeilenende (deutsch, z. B. „Unternehmens-nachfolge“) — gilt auch für Break-up-Briefe
                </label>
                <label className="flex items-start gap-2 text-xs text-foreground cursor-pointer">
                  <input type="checkbox" checked={form.auto_fit_one_page !== false}
                    onChange={e => setField('auto_fit_one_page', e.target.checked)}
                    data-testid="print-autofit-check" className="accent-purple-500 w-3.5 h-3.5 mt-0.5" />
                  <span>
                    Auto-Fit: Der Block „Guten Tag …“ bis Unterschrift steht vertikal zentriert zwischen Datum und Textgrenze; ist er zu hoch, rückt er nur so weit wie nötig nach oben (höchstens auf die Datumszeile),
                    dann wird die Unterschrift bis 20 % kleiner, zuletzt Schrift, Zeilen- und Absatzabstand proportional (nicht unter „min. Schrift“) — die Unterschrift steht immer mit dem eingestellten Abstand unter dem letzten Absatz; gilt auch für Break-up-Briefe.
                    {form.auto_fit_one_page !== false && <span className="text-muted-foreground"> Fix gezogene Text- und Unterschrift-Positionen aus dem Layout werden dabei ignoriert (am einzelnen Brief gezogene zählen).</span>}
                    {form.auto_fit_one_page !== false && Number(form.auto_fit_min_font) >= Number(form.font_size) && (
                      <span className="block text-amber-300 mt-0.5" data-testid="print-autofit-min-warning">
                        „min. Schrift“ ({form.auto_fit_min_font} mm) ist nicht kleiner als die Font size ({form.font_size} mm) — Auto-Fit kann so nichts verkleinern.
                      </span>
                    )}
                  </span>
                </label>
                <label className="flex items-start gap-2 text-xs text-foreground cursor-pointer">
                  <input type="checkbox" checked={form.body_beside_date !== false}
                    onChange={e => setField('body_beside_date', e.target.checked)}
                    data-testid="print-body-beside-date-check" className="accent-purple-500 w-3.5 h-3.5 mt-0.5" />
                  <span>
                    Platz gewinnen, wenn der Brief sonst nicht auf eine Seite passt: Der Text darf so weit wie nötig nach oben rücken, bis die Anrede („Guten Tag …“) links neben dem Datum auf dessen Zeile steht
                    — erst danach werden Unterschrift bzw. Schrift verkleinert. Passt der Brief ohnehin, bleibt der Text zentriert unter dem Datum.
                    {form.body_beside_date !== false && <span className="text-muted-foreground"> Eine am einzelnen Brief gezogene Text-Position wird nie verschoben.</span>}
                  </span>
                </label>
                <div>
                  <label className="text-[10px] uppercase tracking-wider font-mono text-muted-foreground">Header line (optional, written above the letter)</label>
                  <Input value={form.header_text || ''} onChange={e => setField('header_text', e.target.value)}
                    placeholder="Haben Sie schon einmal über Ihre Nachfolge nachgedacht?"
                    data-testid="print-header-input" className="mt-1 h-8 text-xs rounded-sm" />
                </div>
              </div>
            )}

            {tab === 'flyer' && (
              <p className="text-[11px] text-muted-foreground border border-border rounded-sm p-3">
                The message text is chosen automatically: <span className="text-foreground">video message</span> for video
                flyers, otherwise the <span className="text-foreground">landing message</span>. “→ bitte wenden” is written
                next to the QR code — drag both boxes in the preview to reposition.
              </p>
            )}
            {tab === 'bletter' && (
      <div className="space-y-3">
        <p className="text-[11px] text-muted-foreground border border-border rounded-sm p-3" data-testid="print-bletter-info">
          <span className="text-foreground">Break-up-Briefe</span> werden mit <span className="text-foreground">eigenen
          Schreib-Einstellungen</span> auf deine Briefpapier-Druckvorlage geschrieben (QR-Feld unten rechts bleibt frei).
          Die Vorschau zeigt den Standard-Text in der Handschrift — bearbeitet wird der Text über den
          „Standard-Text"-Button in der Freitags-Karte (Approvals).
        </p>
        <div className="border border-sky-500/30 rounded-sm p-3 space-y-2" data-testid="print-bl-qr-box">
          <p className="text-[11px] uppercase tracking-wider font-mono text-sky-300 flex items-center gap-1.5">
            <QrCode className="w-3.5 h-3.5" /> QR-Overlay-Kalibrierung (Break-up)
          </p>
          <p className="text-[10px] text-muted-foreground">
            Eigener Versatz &amp; Feldgröße <span className="text-foreground">nur für das Break-up-Briefpapier</span> —
            unabhängig von der Flyer-Kalibrierung. Werte oben unter „QR Versatz X/Y" (positiv = rechts/unten) und
            „QR-Feld Größe" anpassen, dann Testseite auf EIN Briefpapier drucken: Der Rahmen muss das weiße
            QR-Feld unten rechts exakt umschließen.
          </p>
          <Button size="sm" variant="outline" onClick={downloadBlQrTest} disabled={blQrBusy}
            data-testid="print-bl-qr-test-btn"
            className="h-7 text-[11px] rounded-sm border-amber-500/40 text-amber-300 hover:bg-amber-500/10">
            {blQrBusy ? <Loader2 className="w-3 h-3 animate-spin" /> : <Crosshair className="w-3 h-3" />}
            Testseite laden (speichert die Werte)
          </Button>
        </div>
      </div>
    )}
    {tab === 'envelope' && (
              <>
              <EnvelopeFormatPicker form={form} setField={setField} prefix="env" testId="print-env" />
              <div className="border border-border rounded-sm p-3 space-y-2.5">
                <p className="text-[11px] text-muted-foreground">
                  The recipient (owner name + private address from the CRM) is written on the C4 envelope
                  (first outreach letters). Drag the box in the preview to reposition the address block.
                </p>
                <div>
                  <label className="text-[10px] uppercase tracking-wider font-mono text-muted-foreground">Auto-Fit — Ausrichtung der Adresse</label>
                  <select value={form.env_anchor || 'right'} data-testid="print-env-anchor-select"
                    onChange={e => setField('env_anchor', e.target.value)}
                    className="mt-1 w-full h-8 text-xs rounded-sm border border-border bg-secondary/40 text-foreground px-2">
                    <option value="right">Rechts an der Kante — längste Zeile endet am rechten Rand (kurze Adressen weiter rechts, lange weiter links)</option>
                    <option value="left">Feste X-Position — nur bei Bedarf nach links rücken</option>
                  </select>
                  <p className="text-[10px] text-muted-foreground mt-1">Eine per Drag gesetzte Position eines einzelnen Briefs bleibt immer fest.</p>
                </div>
              </div>
              </>
            )}
            {tab === 'benv' && (
              <>
              <EnvelopeFormatPicker form={form} setField={setField} prefix="benv" testId="print-benv" />
              <div className="border border-border rounded-sm p-3 space-y-2.5">
                <p className="text-[11px] text-muted-foreground">
                  <span className="text-foreground">Break-up-Briefe</span> gehen in einen eigenen (kleineren) Umschlag —
                  Format und Einzug oben wählen. Bei hochkantem Einzug wird die Adresse gedreht geschrieben, sodass sie
                  auf dem Umschlag normal lesbar ist. Position über „Adresse X/Y" (in Leserichtung des Umschlags) oder
                  die grüne „Recipient“-Box in der Vorschau ziehen — bei Auto-Fit „rechts an der Kante“ zählt dabei nur die Höhe.
                </p>
                <div>
                  <label className="text-[10px] uppercase tracking-wider font-mono text-muted-foreground">Auto-Fit — Ausrichtung der Adresse</label>
                  <select value={form.benv_anchor || 'right'} data-testid="print-benv-anchor-select"
                    onChange={e => setField('benv_anchor', e.target.value)}
                    className="mt-1 w-full h-8 text-xs rounded-sm border border-border bg-secondary/40 text-foreground px-2">
                    <option value="right">Rechts an der Kante — längste Zeile endet am rechten Rand (kurze Adressen weiter rechts, lange weiter links)</option>
                    <option value="left">Feste X-Position („Adresse X“) — nur bei Bedarf nach links rücken</option>
                  </select>
                </div>
              </div>
              </>
            )}

            {tab === 'letter' && (
              <>
                <div className="border border-border rounded-sm p-3 space-y-3" data-testid="print-devices-manager">
                  <div className="flex items-center justify-between gap-2">
                    <p className="text-[11px] uppercase tracking-wider font-mono text-muted-foreground">iAuto-Geräte / Rechner</p>
                    <Button size="sm" variant="outline" onClick={addDevice} data-testid="print-add-device-btn"
                      className="h-7 text-[11px] rounded-sm border-border">
                      <Plus className="w-3 h-3" /> Rechner
                    </Button>
                  </div>
                  {(settings?.devices || []).filter(d => !d.host_id).map(d => (
                    <div key={d.id} className="border border-border/60 rounded-sm p-2.5 space-y-2" data-testid={`print-device-${d.id}`}>
                      <div className="flex items-center gap-2 flex-wrap">
                        <span className={`w-1.5 h-1.5 rounded-full shrink-0 ${d.online ? (d.api_state && d.api_state.state !== 'ok' ? 'bg-amber-400' : 'bg-emerald-400') : 'bg-zinc-500'}`}
                          title={d.online ? (d.api_state && d.api_state.state !== 'ok' ? 'Agent online — iAuto-API antwortet nicht' : 'Agent online') : 'Agent offline'} />
                        <Input defaultValue={d.name} onBlur={e => saveDeviceField(d, 'name', e.target.value)}
                          data-testid={`print-device-name-${d.id}`} className="h-7 text-xs rounded-sm max-w-[170px]" />
                        {d.online && d.api_state && d.api_state.state !== 'ok' && (
                          <span className="text-[10px] font-mono px-2 py-0.5 rounded-full border text-amber-300 border-amber-500/40 bg-amber-500/10 max-w-[260px] truncate"
                            title={d.api_state.msg || ''} data-testid={`print-device-api-state-${d.id}`}>
                            {d.api_state.state === 'wrong' ? 'falscher Server unter der Adresse' : d.api_state.state === 'none' ? 'keine iAuto-Adresse' : 'iAuto-API nicht erreichbar'} · Queue wartet
                          </span>
                        )}
                        {d.active
                          ? <span className="text-[10px] font-mono px-2 py-0.5 rounded-full border text-purple-300 border-purple-500/40 bg-purple-500/10"
                              data-testid={`print-device-active-${d.id}`}>aktiv</span>
                          : <Button size="sm" variant="outline" onClick={() => activateDevice(d)} data-testid={`print-device-activate-${d.id}`}
                              className="h-6 text-[10px] rounded-sm border-border px-2">aktivieren</Button>}
                        <span className="ml-auto inline-flex items-center gap-1">
                          <button onClick={() => downloadDeviceScript(d)} title="Agent für Windows (Python-Skript) herunterladen — ein Skript bedient alle Maschinen dieses Rechners"
                            data-testid={`print-device-download-${d.id}`} className="inline-flex items-center gap-1 px-1.5 h-6 text-[10px] rounded-sm border border-border text-muted-foreground hover:text-foreground">
                            <Download className="w-3 h-3" /> Windows
                          </button>
                          <button onClick={() => downloadDeviceScript(d, 'mac')} title="Agent für Mac (.command — Doppelklick öffnet Terminal) herunterladen"
                            data-testid={`print-device-download-mac-${d.id}`} className="inline-flex items-center gap-1 px-1.5 h-6 text-[10px] rounded-sm border border-border text-muted-foreground hover:text-foreground">
                            <Download className="w-3 h-3" /> Mac
                          </button>
                        </span>
                        {(settings?.devices || []).filter(x => !x.host_id).length > 1 && (
                          <button onClick={() => deleteDevice(d)} title="Rechner (mit seinen Maschinen) löschen"
                            data-testid={`print-device-delete-${d.id}`} className="p-1 text-muted-foreground hover:text-red-400">
                            <X className="w-3.5 h-3.5" />
                          </button>
                        )}
                      </div>
                      <div className="grid grid-cols-3 gap-2">
                        <div className="col-span-3 sm:col-span-1">
                          <label className="text-[9px] uppercase tracking-wider font-mono text-muted-foreground">iAuto-Adresse</label>
                          <Input key={d.iauto_url || ''} defaultValue={d.iauto_url || ''} onBlur={e => saveDeviceField(d, 'iauto_url', e.target.value)}
                            placeholder="Tool → API der Software, z. B. http://192.168.178.25:90" data-testid={`print-device-url-${d.id}`}
                            title="Adresse der UUNA TEK-Software auf dem Rechner, an dem der Agent läuft (Menü Tool → API, Port 90) — nicht die IP der Maschine. Fehlender Port :90 wird ergänzt."
                            className="h-7 text-[11px] rounded-sm mt-0.5" />
                        </div>
                        <div>
                          <label className="text-[9px] uppercase tracking-wider font-mono text-muted-foreground">X-Offset (mm)</label>
                          <Input type="number" step="0.5" defaultValue={d.xpos_mm ?? ''} onBlur={e => saveDeviceField(d, 'xpos_mm', e.target.value)}
                            placeholder="Standard" data-testid={`print-device-xpos-${d.id}`} className="h-7 text-[11px] rounded-sm mt-0.5" />
                        </div>
                        <div>
                          <label className="text-[9px] uppercase tracking-wider font-mono text-muted-foreground">Y-Offset (mm)</label>
                          <Input type="number" step="0.5" defaultValue={d.ypos_mm ?? ''} onBlur={e => saveDeviceField(d, 'ypos_mm', e.target.value)}
                            placeholder="Standard" data-testid={`print-device-ypos-${d.id}`} className="h-7 text-[11px] rounded-sm mt-0.5" />
                        </div>
                        {d.iauto_url_found && d.iauto_url_found !== d.iauto_url && (
                          <div className="col-span-3 flex items-center gap-2 flex-wrap text-[10px] text-amber-300" data-testid={`print-device-url-found-${d.id}`}>
                            <span>Agent: nichts unter <span className="font-mono">{d.iauto_url || 'der Adresse'}</span> — die iAuto-API antwortet unter <span className="font-mono">{d.iauto_url_found}</span></span>
                            <button onClick={() => saveDeviceField(d, 'iauto_url', d.iauto_url_found)} data-testid={`print-device-url-adopt-${d.id}`}
                              className="px-1.5 h-5 rounded-sm border border-amber-500/40 hover:bg-amber-500/10">Übernehmen</button>
                          </div>
                        )}
                      </div>
                      <div className="flex items-center gap-2 flex-wrap" data-testid={`print-device-machine-row-${d.id}`}>
                        <label className="text-[9px] uppercase tracking-wider font-mono text-muted-foreground shrink-0">Maschine</label>
                        <select value={d.machine_id || ''} onChange={(e) => saveDeviceField(d, 'machine_id', e.target.value)} data-testid={`print-device-machine-${d.id}`}
                          className="h-7 text-[11px] rounded-sm bg-secondary border border-border px-2 text-foreground max-w-full">
                          <option value="">automatisch (erste freie — bei Fehler wechselt der Agent)</option>
                          {machineOptions(d).map((m) => (
                            <option key={m.mid} value={m.mid}>{m.mid}{m.status ? ` · ${m.status}` : ''}{m.waiting != null ? ` · ${m.waiting} wartend` : ''}</option>
                          ))}
                        </select>
                        <span className="text-[10px] text-muted-foreground">
                          {machineOptions(d).length > 1
                            ? (machinesOf(d).length > 0 ? 'Mehrere Maschinen an diesem Rechner — jeder Maschine hier ihre iAuto zuweisen.' : 'Die iAuto-Software kennt mehrere Maschinen — hier die wirklich angeschlossene festlegen (HTTP 500 = falsche Maschine).')
                            : machineOptions(d).length === 0 ? 'Liste erscheint, sobald der Agent einmal gelaufen ist.' : 'Nur eine Maschine bekannt — „automatisch“ reicht.'}
                        </span>
                      </div>
                      {machinesOf(d).length > 0 && (
                        <div className="border border-purple-500/20 rounded-sm divide-y divide-border/60" data-testid={`print-device-machines-${d.id}`}>
                          {machinesOf(d).map((m) => (
                            <div key={m.id} className="flex items-center gap-2 flex-wrap px-2 py-1.5" data-testid={`print-device-${m.id}`}>
                              <span className="text-[9px] uppercase tracking-wider font-mono text-purple-300 shrink-0">2. Maschine</span>
                              <Input defaultValue={m.name} onBlur={e => saveDeviceField(m, 'name', e.target.value)}
                                data-testid={`print-device-name-${m.id}`} className="h-7 text-xs rounded-sm max-w-[170px]" />
                              {m.active
                                ? <span className="text-[10px] font-mono px-2 py-0.5 rounded-full border text-purple-300 border-purple-500/40 bg-purple-500/10" data-testid={`print-device-active-${m.id}`}>aktiv</span>
                                : <Button size="sm" variant="outline" onClick={() => activateDevice(m)} data-testid={`print-device-activate-${m.id}`} className="h-6 text-[10px] rounded-sm border-border px-2">aktivieren</Button>}
                              <select value={m.machine_id || ''} onChange={(e) => saveDeviceField(m, 'machine_id', e.target.value)} data-testid={`print-device-machine-${m.id}`}
                                title="Welche iAuto der Software diese Maschine ist"
                                className={`h-7 text-[11px] rounded-sm bg-secondary border px-2 text-foreground max-w-[220px] ${m.machine_id ? 'border-border' : 'border-amber-500/50'}`}>
                                <option value="">Maschine wählen …</option>
                                {machineOptions(m).map((o) => (
                                  <option key={o.mid} value={o.mid}>{o.mid}{o.status ? ` · ${o.status}` : ''}</option>
                                ))}
                              </select>
                              <span className="inline-flex items-center gap-1 text-[9px] uppercase tracking-wider font-mono text-muted-foreground">
                                X <Input type="number" step="0.5" defaultValue={m.xpos_mm ?? ''} onBlur={e => saveDeviceField(m, 'xpos_mm', e.target.value)} placeholder="Std" data-testid={`print-device-xpos-${m.id}`} className="h-7 w-16 text-[11px] rounded-sm" />
                                Y <Input type="number" step="0.5" defaultValue={m.ypos_mm ?? ''} onBlur={e => saveDeviceField(m, 'ypos_mm', e.target.value)} placeholder="Std" data-testid={`print-device-ypos-${m.id}`} className="h-7 w-16 text-[11px] rounded-sm" />
                              </span>
                              {m.queued ? <span className="text-[10px] text-muted-foreground" data-testid={`print-device-queued-${m.id}`}>{m.queued} in Queue</span> : null}
                              <button onClick={() => deleteDevice(m)} title="Maschine löschen" data-testid={`print-device-delete-${m.id}`}
                                className="ml-auto p-1 text-muted-foreground hover:text-red-400"><X className="w-3.5 h-3.5" /></button>
                            </div>
                          ))}
                        </div>
                      )}
                      <button onClick={() => addMachine(d)} data-testid={`print-device-add-machine-${d.id}`}
                        title="Zweite iAuto am selben Rechner (USB oder gleiches WLAN): eigene Queue, eigene Maschine, gleicher Agent"
                        className="inline-flex items-center gap-1 px-1.5 h-6 text-[10px] rounded-sm border border-dashed border-purple-500/40 text-purple-200 hover:bg-purple-500/10">
                        <Plus className="w-3 h-3" /> Maschine an diesem Rechner
                      </button>
                      <div className="flex items-center gap-2 flex-wrap" data-testid={`print-device-login-row-${d.id}`}>
                        <label className="text-[9px] uppercase tracking-wider font-mono text-muted-foreground shrink-0"
                          title="Konto der UUNA TEK-Software (Menü User). Meldet sich das Programm nachts ab (Code 2), loggt der Agent sich damit selbst wieder ein — Passwort wird nur an den Agent dieses Rechners gegeben.">
                          UUNA TEK Login
                        </label>
                        <Input key={`uin-${d.iauto_uin || ''}`} defaultValue={d.iauto_uin || ''} onBlur={e => saveDeviceField(d, 'iauto_uin', e.target.value)}
                          placeholder="Benutzer / E-Mail" data-testid={`print-device-uin-${d.id}`} className="h-7 w-44 text-[11px] rounded-sm" autoComplete="off" />
                        <Input type="password" defaultValue="" onBlur={e => { if (e.target.value) { saveDeviceField(d, 'iauto_passwd', e.target.value); e.target.value = ''; } }}
                          placeholder={d.iauto_login_set ? '•••••• (gespeichert)' : 'Passwort'} data-testid={`print-device-passwd-${d.id}`}
                          className="h-7 w-36 text-[11px] rounded-sm" autoComplete="new-password" />
                        <span className={`text-[10px] ${d.iauto_login_set ? 'text-emerald-300' : 'text-amber-300'}`} data-testid={`print-device-login-state-${d.id}`}>
                          {d.iauto_login_set ? 'Auto-Relogin aktiv (Agent v3.2+)' : 'ohne Login wartet die Queue nach einer Abmeldung, bis jemand von Hand einloggt'}
                        </span>
                      </div>
                      <div className="flex items-center gap-2" data-testid={`print-device-mac-hint-${d.id}`}>
                        <span className="text-[9px] uppercase tracking-wider font-mono text-muted-foreground shrink-0">Mac · Start</span>
                        <code className="text-[10px] text-foreground font-mono bg-secondary/40 px-2 py-1 rounded-sm truncate flex-1" title={macFirstRun(d)}>{macFirstRun(d)}</code>
                        <button onClick={() => copyText(macFirstRun(d))} className="p-1 text-muted-foreground hover:text-foreground" title="Terminal-Befehl kopieren (startet die neueste heruntergeladene .command — ohne Gatekeeper/chmod)"
                          data-testid={`print-device-copy-mac-${d.id}`}><Copy className="w-3.5 h-3.5" /></button>
                      </div>
                      <div className="flex items-center gap-2">
                        <code className="text-[10px] text-foreground font-mono bg-secondary/40 px-2 py-1 rounded-sm truncate flex-1"
                          data-testid={`print-device-key-${d.id}`}>{d.agent_key}</code>
                        <button onClick={() => copyText(d.agent_key)} className="p-1 text-muted-foreground hover:text-foreground"
                          title="Key kopieren" data-testid={`print-device-copy-key-${d.id}`}>
                          <Copy className="w-3.5 h-3.5" />
                        </button>
                        <Button size="sm" variant="outline" onClick={() => regenDeviceKey(d)} data-testid={`print-device-regen-${d.id}`}
                          className="h-6 text-[10px] rounded-sm border-border px-2">
                          <RefreshCw className="w-3 h-3" /> Key
                        </Button>
                      </div>
                      <DeviceAgentLog device={d} scriptVersion={settings?.agent_script_version} />
                    </div>
                  ))}
                  <p className="text-[10px] text-muted-foreground leading-relaxed">
                    <span className="text-foreground">Ein Rechner = ein Agent.</span> Jede Serie wählt beim Vorbereiten ihr Zielgerät („Schreiben auf“); ohne Auswahl geht es an das <span className="text-foreground">aktive</span> Gerät. Mehrere Maschinen am selben Rechner („Maschine an diesem Rechner“) werden vom <span className="text-foreground">einen</span> Agent parallel bedient — jede mit eigener Queue, eigener iAuto und Live-Status (z. B. Break-up-Briefe auf A, Erstansprache auf B).
                    Agent starten: <span className="text-foreground">Windows</span> = <span className="font-mono">python olta_print_agent_….py</span>, <span className="text-foreground">Mac</span> = .command laden und „Mac · Start“ ins Terminal (Python 3 via <span className="font-mono">xcode-select --install</span>).
                    Die iAuto-Adresse ist der API-Server der UUNA TEK-<span className="text-foreground">Software</span> auf diesem Rechner (Tool → API zeigt sie; Port 90 wird ergänzt) — <span className="text-foreground">nicht</span> die IP der Maschine. USB/Windows meist <span className="font-mono">http://127.0.0.1:90</span>; antwortet dort nichts, sucht der Agent selbst (LAN-IPs, alle Ports) und bietet die gefundene Adresse zum Übernehmen an.
                    <span className="text-foreground"> Zwei iAutos an einem PC — so geht’s:</span> (1) beide Maschinen per USB (oder im selben WLAN) an den PC, in der UUNA TEK-Software unter Select Machine beide verbinden, Tool → API an; (2) hier „Maschine an diesem Rechner“ klicken; (3) Agent einmal laufen lassen → unter „Maschine“ erscheinen beide IDs → dem Rechner Maschine A, der 2. Maschine B zuweisen; (4) „Verbindungstest“ schreibt pro Maschine einen kurzen Teststrich — schreiben beide, ist alles bereit; (5) Serien mit „Schreiben auf“ verteilen.
                    <span className="text-foreground"> Fehlersuche:</span> „Agent-Log“ zeigt live, was der Agent im Terminal meldet (Zeilen der 2. Maschine mit ihrem Namen in Klammern). Bei <span className="font-mono">HTTP 500</span> zuerst die UUNA TEK-Software aktualisieren (Mac: UUNA_KAT 4.1 vom 26.09.2026) und den Agent neu laden (v{settings?.agent_script_version || '3.2'}).
                    <span className="text-foreground"> Nachtbetrieb (Agent v3.2):</span> hält den Rechner wach, loggt sich nach einer Abmeldung der Software (Code 2) mit dem hinterlegten UUNA TEK Login selbst wieder ein und <span className="text-foreground">wartet</span>, wenn die Maschine aus der Software verschwindet oder der Upload zur Maschine hängt („Please wait … upload“-Fenster) — statt Briefe auf Fehler zu setzen; hängt ein Upload über 4 Min., bricht er den Task ab und sendet neu (nach 3× wird der Brief geparkt, die Serie läuft weiter). Der WLAN-Upload zur Maschine ist die Schwachstelle — für lange Serien über Nacht die Maschinen per <span className="text-foreground">USB</span> anschließen.
                  </p>
                </div>
                <label className="flex items-center gap-2 text-xs text-foreground cursor-pointer">
                  <input type="checkbox" checked={form.auto_start !== false}
                    onChange={e => setForm(f => ({ ...f, auto_start: e.target.checked }))}
                    data-testid="print-autostart-check" className="accent-purple-500 w-3.5 h-3.5" />
                  Start writing immediately (otherwise the job is only loaded on the machine) — nur ältere iAuto-Software; die 2026er API startet jeden Auftrag selbst
                </label>

                <div className="border border-border rounded-sm p-3 space-y-2">
                  <p className="text-[11px] uppercase tracking-wider font-mono text-muted-foreground">Handwriting font</p>
                  <div className="flex items-center gap-2 flex-wrap">
                    <span className="text-xs text-foreground font-medium" data-testid="print-dialog-font-name">{settings?.font_name}</span>
                    <input type="file" accept=".ttf,.otf,.gfont" ref={fileRef} onChange={uploadFont} className="hidden" data-testid="print-font-file-input" />
                    <Button size="sm" variant="outline" onClick={() => fileRef.current?.click()} data-testid="print-font-upload-btn"
                      className="h-7 text-[11px] rounded-sm border-border ml-auto">
                      <Upload className="w-3 h-3" /> Upload TTF/OTF/.gfont
                    </Button>
                    {settings?.has_custom_font && (
                      <Button size="sm" variant="outline" onClick={removeFont} data-testid="print-font-remove-btn"
                        className="h-7 text-[11px] rounded-sm border-border text-red-400">
                        <X className="w-3 h-3" /> Remove
                      </Button>
                    )}
                  </div>
                </div>
              </>
            )}

            <div className="flex justify-end gap-2 pt-1">
              {tab === 'letter' && (
                <Button size="sm" variant="outline" onClick={sendTestLetter} disabled={testing}
                  data-testid="print-test-letter-btn"
                  className="h-8 text-xs rounded-sm border-amber-500/40 text-amber-300 hover:bg-amber-500/10 mr-auto">
                  {testing ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : <FlaskConical className="w-3.5 h-3.5" />}
                  Testbrief schreiben
                </Button>
              )}
              <Button size="sm" variant="outline" onClick={() => onOpenChange(false)}
                className="h-8 text-xs rounded-sm border-border">Cancel</Button>
              <Button size="sm" onClick={saveSettings} disabled={saving} data-testid="print-settings-save-btn"
                className="h-8 text-xs rounded-sm bg-purple-600 hover:bg-purple-500 text-white">
                {saving ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : null} Save
              </Button>
            </div>
          </div>

          <div className="space-y-2">
            <div className="flex items-center gap-2">
              <p className="text-[11px] uppercase tracking-wider font-mono text-muted-foreground flex items-center gap-2">
                Live preview {previewLoading && <Loader2 className="w-3 h-3 animate-spin" />}
              </p>
              {estSeconds > 0 && (
                <span className="flex items-center gap-1 text-[10px] font-mono px-2 py-0.5 rounded-full border text-sky-300 border-sky-500/40 bg-sky-500/10"
                  data-testid="print-preview-est" title="Geschätzte Schreibzeit der Maschine (alle Seiten, abhängig von Pen speed)">
                  <Timer className="w-3 h-3" />
                  ~{estSeconds >= 90 ? `${Math.ceil(estSeconds / 60)} min` : `${estSeconds} s`}
                  {tab === 'letter' || tab === 'bletter' ? ' pro Brief' : tab === 'flyer' ? ' pro Flyer' : ' pro Umschlag'}
                  {previewPages > 1 ? ` (${previewPages} S.)` : ''}
                </span>
              )}
              {(tab === 'letter' || tab === 'bletter') && previewFit?.applied && (
                <span className="text-[10px] font-mono px-2 py-0.5 rounded-full border text-emerald-300 border-emerald-500/40 bg-emerald-500/10"
                  data-testid="print-preview-fit" title="Auto-Fit: Schrift proportional verkleinert, damit Brief + Unterschrift auf eine Seite passen">
                  Auto-Fit {previewFit.signature_scale < 1 ? `Unterschrift ${Math.round(previewFit.signature_scale * 100)} %` : `${previewFit.font_size} mm`}
                </span>
              )}
              <button onClick={toggleCompare} data-testid="print-compare-toggle"
                title="Direktvergleich: gleicher Text ohne Zeilenstart-Versatz, Baseline-Welle, Wort- & Zeilenabstand-Variation"
                className={`flex items-center gap-1 text-[10px] font-mono px-2 py-0.5 rounded-full border transition-colors ${compareOff
                  ? 'text-amber-300 border-amber-500/50 bg-amber-500/15'
                  : 'text-muted-foreground border-border hover:text-foreground hover:border-foreground/40'}`}>
                {compareOff ? 'VORHER (ohne Humanisierung)' : 'Vorher/Nachher'}
              </button>
              {hasCustomPos && (
                <Button size="sm" variant="outline" onClick={resetPositions} data-testid="print-reset-positions-btn"
                  className="h-6 text-[10px] rounded-sm border-border ml-auto">
                  <Undo2 className="w-3 h-3" /> Reset positions
                </Button>
              )}
            </div>
            {tab === 'letter' && (
              <div className="flex items-center gap-1.5">
                <select value={form.preview_letter_id || ''} data-testid="print-preview-letter-select"
                  onChange={e => {
                    pageNumRef.current = 1; setPreviewPage(1);
                    setField('preview_letter_id', e.target.value);
                  }}
                  className="flex-1 h-7 text-[11px] rounded-sm border border-border bg-secondary/40 text-foreground px-2">
                  <option value="">Vorschau-Text: Standardbrief (volle Länge)</option>
                  {previewLetters.map(l => (
                    <option key={l.id} value={l.id}>Vorschau-Text: Brief an {l.company_name}</option>
                  ))}
                </select>
                {previewPages > 1 && (
                  <span className="flex items-center gap-0.5 text-[10px] text-muted-foreground font-mono shrink-0">
                    <button onClick={() => goPage(Math.max(1, previewPage - 1))} disabled={previewPage <= 1}
                      className="p-0.5 hover:text-foreground disabled:opacity-30" data-testid="print-preview-page-prev">
                      <ChevronLeft className="w-3.5 h-3.5" />
                    </button>
                    S. {previewPage}/{previewPages}
                    <button onClick={() => goPage(Math.min(previewPages, previewPage + 1))} disabled={previewPage >= previewPages}
                      className="p-0.5 hover:text-foreground disabled:opacity-30" data-testid="print-preview-page-next">
                      <ChevronRight className="w-3.5 h-3.5" />
                    </button>
                  </span>
                )}
              </div>
            )}
            <div ref={pageRef} className="relative bg-white rounded-sm border border-border overflow-hidden select-none"
              style={{ aspectRatio: `${pageDims.w} / ${pageDims.h}` }} data-testid="print-live-preview">
              {(tab === 'letter' || tab === 'bletter') && (
                <img src={tab === 'bletter' ? '/breakup-briefpapier.png' : '/briefpapier.png'} alt="" data-testid="print-settings-letterhead"
                  className="absolute inset-0 w-full h-full pointer-events-none select-none" />
              )}
              {previewSvg
                ? <div className="absolute inset-0" dangerouslySetInnerHTML={{ __html: previewSvg }} />
                : <p className="text-[11px] text-gray-400 p-4">Preview unavailable</p>}
              {(tab === 'letter' || tab === 'bletter') && previewSvg && (
                <div className="absolute border border-dashed border-red-400/40 pointer-events-none" data-testid="print-margin-guides"
                  style={{
                    left: `${(parseFloat(form[tab === 'bletter' ? 'bl_margin_left' : 'margin_left'] || 0) / pageDims.w) * 100}%`,
                    top: `${(parseFloat(form[tab === 'bletter' ? 'bl_margin_top' : 'margin_top'] || 0) / pageDims.h) * 100}%`,
                    right: `${(parseFloat(form[tab === 'bletter' ? 'bl_margin_right' : 'margin_right'] || 0) / pageDims.w) * 100}%`,
                    bottom: `${(parseFloat(form[tab === 'bletter' ? 'bl_margin_bottom' : 'margin_bottom'] || 0) / pageDims.h) * 100}%`,
                  }} />
              )}
              {visibleElements.map(key => {
                const el = elFor(key);
                const pos = drag && drag.key === key ? drag : el;
                const draggable = key === 'body' || key === 'bl_body' || !!ELEMENTS[key].posKey;
                return (
                  <div key={key} onPointerDown={draggable ? (e) => startDrag(key, e) : undefined} data-testid={`print-drag-${key}`}
                    className={`absolute border border-dashed rounded-[2px] ${draggable ? 'cursor-move' : 'cursor-default'} ${ELEMENTS[key].cls} opacity-50 hover:opacity-100 transition-opacity`}
                    style={{
                      left: `${(pos.x / pageDims.w) * 100}%`, top: `${(pos.y / pageDims.h) * 100}%`,
                      width: `${(el.w / pageDims.w) * 100}%`, height: `${(el.h / pageDims.h) * 100}%`,
                    }}>
                    <span className={`absolute -top-[14px] left-0 text-[8px] font-mono px-1 rounded-sm text-white whitespace-nowrap ${ELEMENTS[key].badge}`}>
                      {ELEMENTS[key].label}
                    </span>
                  </div>
                );
              })}
            </div>
            <p className="text-[10px] text-muted-foreground">
              Drag the colored boxes to reposition. The preview updates as you change values — even before saving.
            </p>
          </div>
        </div>
      </DialogContent>
    </Dialog>
  );
};

export default PrintSettingsDialog;
